import useBreakPoinst from "@hooks/useBreakPoinst";
import dayjs from "dayjs";

import PropertyIcon from "@assets/svg/PropertyIcon.svg?react";
import BathtubIcon from "@assets/svg/Bath.svg?react";
import BedroomIcon from "@assets/svg/Bed.svg?react";
import UserIcon from "@assets/svg/UserIcon.svg?react";
import {
  PaymentSuccessContainer,
  PaymentSuccessTitle,
  PaymentSuccessDescription,
  PaymentSuccessInfoBox,
  PaymentSuccessButton,
  PaymentSuccessBadge,
  PaymentSuccessBadgeText,
  PaymentSuccessIcon,
  PaymentSuccessArrowIcon,
  PaymentSuccessMsoolIcon,
  PaymentSuccessFeaturesContainer,
  PaymentSuccessFeatureItem,
  PaymentSuccessDetailsSection,
  PaymentSuccessPropertyInfoContainer,
  PaymentSuccessPropertyTextContainer,
  PaymentSuccessPropertyTitle,
  PaymentSuccessPropertyLocation,
  PaymentSuccessBookingDetailsContainer,
  PaymentSuccessBookingDetailsTitle,
  PaymentSuccessBookingDetailsText,
  PaymentSuccessBookingGuestsText,
  PaymentSuccessDivider,
} from "./styles";
import InvoiceBreakdown from "./InvoiceBreakdown";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useWelcomeBook } from "@services";
import { useState, useEffect } from "react";
import GuestPortalPaymentFail from "./GuestPortalPaymentFail";
import { Loading } from "@components";

const PaymentStatus = () => {
  const [verifyState, setVerifyState] = useState<
    "loading" | "confirmed" | "pending"
  >("loading");
  const { id } = useParams();
  const location = useLocation();
  const { GetWelcomeBookQuery, verifyPayment } = useWelcomeBook();
  const { data, isLoading } = GetWelcomeBookQuery(id ?? "");
  const result = data?.data?.result;
  const { isPhone } = useBreakPoinst();
  const navigate = useNavigate();
  // Verify payment with the backend on load — the URL's ?status param is not trusted.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const moyasarId = params.get("id");
    if (!id || !moyasarId) {
      setVerifyState("pending");
      return;
    }
    verifyPayment(id, moyasarId)
      .then((res) => {
        const confirmed = res?.data?.result?.confirmed === true;
        setVerifyState(confirmed ? "confirmed" : "pending");
      })
      .catch(() => setVerifyState("pending"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  const title = "تم الدفع بنجاح، وتم تأكيد حجزك!";
  const description = "تم تأكيد الحجز بنجاح! نتطلع إلى استضافتك قريبًا.";
  const infoBox =
    "تم إرسال رسالة تأكيد إلى بريدك الإلكتروني المسجل تحتوي على جميع تفاصيل الحجز.";
  const buttonText = "عرض تفاصيل الحجز";

  // Format dates using dayjs
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("D MMMM YYYY");
  };

  const checkInDate = result?.checkInDate ? formatDate(result.checkInDate) : "";
  const checkOutDate = result?.checkOutDate
    ? formatDate(result.checkOutDate)
    : "";

  if (isLoading || verifyState === "loading") {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loading />
      </div>
    );
  }
  return (
    <>
      {verifyState === "confirmed" ? (
        <PaymentSuccessContainer $isPhone={isPhone}>
          {/* svg iCon */}
          <PaymentSuccessIcon $isPhone={isPhone} />
          <PaymentSuccessTitle $isPhone={isPhone}>{title}</PaymentSuccessTitle>

          <PaymentSuccessDescription $isPhone={isPhone}>
            {description}
          </PaymentSuccessDescription>
          {/* Details section  */}
          <PaymentSuccessDetailsSection>
            <PaymentSuccessPropertyInfoContainer>
              <PropertyIcon width={100} height={100} />
              <PaymentSuccessPropertyTextContainer>
                <PaymentSuccessPropertyTitle>
                  {result?.propertyName}
                </PaymentSuccessPropertyTitle>
                <PaymentSuccessPropertyLocation>
                  {result?.propertyName}
                </PaymentSuccessPropertyLocation>
                <PaymentSuccessFeaturesContainer $isPhone={isPhone}>
                  <PaymentSuccessFeatureItem $isPhone={isPhone}>
                    <BathtubIcon />
                    {result?.bathroomCount} حمامات
                  </PaymentSuccessFeatureItem>
                  <PaymentSuccessFeatureItem $isPhone={isPhone}>
                    <BedroomIcon />
                    {result?.bedCount} أسرّة
                  </PaymentSuccessFeatureItem>
                  <PaymentSuccessFeatureItem $isPhone={isPhone}>
                    <UserIcon />
                    {result?.guestCount} ضيوف
                  </PaymentSuccessFeatureItem>
                </PaymentSuccessFeaturesContainer>
              </PaymentSuccessPropertyTextContainer>
            </PaymentSuccessPropertyInfoContainer>
            <PaymentSuccessDivider />
            <PaymentSuccessBookingDetailsContainer>
              <PaymentSuccessBookingDetailsTitle>
                تفاصيل الحجز
              </PaymentSuccessBookingDetailsTitle>
              <PaymentSuccessBookingDetailsText>
                {checkInDate && checkOutDate
                  ? `${checkInDate} - ${checkOutDate}`
                  : "تاريخ الحجز غير متوفر"}
              </PaymentSuccessBookingDetailsText>
              <PaymentSuccessBookingGuestsText>
                <UserIcon />
                {result?.guestCount} ضيوف
              </PaymentSuccessBookingGuestsText>
            </PaymentSuccessBookingDetailsContainer>
            <PaymentSuccessDivider />

            {/* MS-606: the same backend split the payment page prints (MS-605). Printing
                totalPrice beside the night count folded the VAT into the nightly rate. */}
            <InvoiceBreakdown
              nightlyRate={result?.nightlyRate}
              numberOfNights={result?.numberOfNights}
              subtotal={result?.subtotal}
              vatAmount={result?.vatAmount}
              vatRatePercent={result?.vatRatePercent}
              guestTotal={result?.guestTotal}
              totalPrice={result?.totalPrice}
              isPhone={isPhone}
              title="بيانات الدفع"
              totalLabel="الإجمالي"
            />
          </PaymentSuccessDetailsSection>

          <PaymentSuccessInfoBox $isPhone={isPhone}>
            {infoBox}
          </PaymentSuccessInfoBox>
          <PaymentSuccessButton
            onClick={() => {
              navigate(`/guest/${id}`);
            }}
            $isPhone={isPhone}
          >
            {buttonText}
            <PaymentSuccessArrowIcon $isPhone={isPhone} />
          </PaymentSuccessButton>
          <PaymentSuccessBadge $isPhone={isPhone}>
            <PaymentSuccessBadgeText $isPhone={isPhone}>
              مشغل بواسطة مسؤول
            </PaymentSuccessBadgeText>
            <PaymentSuccessMsoolIcon $isPhone={isPhone} />
          </PaymentSuccessBadge>
        </PaymentSuccessContainer>
      ) : (
        <GuestPortalPaymentFail />
      )}
    </>
  );
};

export default PaymentStatus;
